import type { ReactNode } from 'react';
import { useEffect, useId, useState } from 'react';
import { cn } from '../../common/functions/cn';
import OdinIcon from '../odin-icon/odin-icon.component';

export interface AccordionProps {
  id?: string;
  isOpen: boolean;
  title: string | ReactNode;
  children: ReactNode;
  hidden?: boolean;
  onClick?: (isOpen: boolean) => void;
  wrapperStyles?: string;
  headerStyles?: string;
  childWrapperStyles?: string;
}

export default function Accordion({
  id,
  isOpen,
  title,
  children,
  hidden,
  onClick,
  wrapperStyles,
  headerStyles,
  childWrapperStyles,
}: AccordionProps) {
  const contentWrapperId = useId();
  const [currentlyOpen, setCurrentlyOpen] = useState(isOpen);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Only sync when isOpen prop changes, not when currentlyOpen changes
  useEffect(() => {
    if (isOpen !== currentlyOpen) {
      setCurrentlyOpen(isOpen);
    }
  }, [isOpen]);

  const onAccordionClick = () => {
    onClick?.(!currentlyOpen);
    setCurrentlyOpen(!currentlyOpen);
  };

  const accordionWrapperStyles = cn('mb-4 flex w-full flex-col', {
    hidden: hidden,
  });

  const accordionHeaderStyles = cn(
    'flex w-full cursor-pointer border-border border-b border-solid text-left focus:outline-hidden',
    headerStyles
  );

  const childWrapperStyling = cn('grow pt-5', childWrapperStyles);

  // animated collapse: the grid row squashes the content under overflow-hidden, and the
  // visibility transition keeps collapsed content out of the tab order once it finishes
  const contentWrapperStyles = cn(
    'grid grow transition-[grid-template-rows,visibility] duration-200 ease-out motion-reduce:transition-none',
    currentlyOpen ? 'visible grid-rows-[1fr]' : 'invisible grid-rows-[0fr]'
  );

  return (
    <div id={id} className={cn(accordionWrapperStyles, wrapperStyles)}>
      <button
        type="button"
        className={accordionHeaderStyles}
        onClick={onAccordionClick}
        aria-expanded={currentlyOpen}
        aria-controls={contentWrapperId}
      >
        <div className="grow font-semibold text-lg leading-9">{title}</div>
        <div className="flex shrink items-center pr-px">
          <OdinIcon icon={currentlyOpen ? 'ChevronUp' : 'ChevronDown'} className="size-5" />
        </div>
      </button>
      <div id={contentWrapperId} className={contentWrapperStyles}>
        <div className={cn('min-h-0 min-w-0', { 'overflow-hidden': !currentlyOpen })}>
          <div className={childWrapperStyling}>{children}</div>
        </div>
      </div>
    </div>
  );
}
